import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {API, isMobile, showError, showInfo, showSuccess, verifyJSON, getChannelModels} from '../../helpers';
import {CHANNEL_OPTIONS} from '../../constants';
import Title from "@douyinfe/semi-ui/lib/es/typography/title";
import {SideSheet, Space, Spin, Button, Input, Typography, Select, TextArea, Checkbox, Banner} from "@douyinfe/semi-ui";

const MODEL_MAPPING_EXAMPLE = {
    'gpt-3.5-turbo-0301': 'gpt-3.5-turbo',
    'gpt-4-0314': 'gpt-4',
    'gpt-4-32k-0314': 'gpt-4-32k'
};

function type2secretPrompt(type) {
    switch (type) {
        case 15:
            return '按照如下格式输入：APIKey|SecretKey';
        case 18:
            return '按照如下格式输入：APPID|APISecret|APIKey';
        case 22:
            return '按照如下格式输入：APIKey-AppId，例如：fastgpt-0sp2gtvfdgyi4k30jwlgwf1i-64f335d84283f05518e9e041';
        case 23:
            return '按照如下格式输入：AppId|SecretId|SecretKey';
        default:
            return '请输入渠道对应的鉴权密钥';
    }
}

const EditChannel = (props) => {
    const navigate = useNavigate();
    const channelId = props.editingChannel.id;
    const isEdit = channelId !== undefined;
    const [loading, setLoading] = useState(isEdit);
    const handleCancel = () => {
        props.handleClose();
    };
    const originInputs = {
        name: '',
        type: 1,
        key: '',
        openai_organization: '',
        base_url: '',
        other: '',
        model_mapping: '',
        system_prompt: '',
        models: [],
        auto_ban: 1,
        groups: ['default']
    };
    const [batch, setBatch] = useState(false);
    const [autoBan, setAutoBan] = useState(true);
    const [inputs, setInputs] = useState(originInputs);
    const [originModelOptions, setOriginModelOptions] = useState([]);
    const [modelOptions, setModelOptions] = useState([]);
    const [groupOptions, setGroupOptions] = useState([]);
    const [basicModels, setBasicModels] = useState([]);
    const [fullModels, setFullModels] = useState([]);
    const [customModel, setCustomModel] = useState('');
    const [config, setConfig] = useState({
        region: '',
        sk: '',
        ak: '',
        user_id: '',
        vertex_ai_project_id: '',
        vertex_ai_adc: '',
    });

    const handleInputChange = (name, value) => {
        setInputs((inputs) => ({...inputs, [name]: value}));
        if (name === 'type') {
            let localModels = getChannelModels(value);
            if (inputs.models.length === 0) {
                setInputs((inputs) => ({...inputs, models: localModels}));
            }
            setBasicModels(localModels);
        }
    };

    const handleConfigChange = (name, value) => {
        setConfig((config) => ({...config, [name]: value}));
    };

    const loadChannel = async () => {
        setLoading(true);
        let res = await API.get(`/api/channel/${channelId}`);
        const {success, message, data} = res.data;
        if (success) {
            if (data.models === '') {
                data.models = [];
            } else {
                data.models = data.models.split(',');
            }
            if (data.group === '') {
                data.groups = [];
            } else {
                data.groups = data.group.split(',');
            }
            if (data.model_mapping !== '') {
                data.model_mapping = JSON.stringify(JSON.parse(data.model_mapping), null, 2);
            }
            setInputs(data);
            if (data.config !== '') {
                try {
                    setConfig(JSON.parse(data.config));
                } catch (e) {}
            }
            if (data.auto_ban === 0) {
                setAutoBan(false);
            } else {
                setAutoBan(true);
            }
            setBasicModels(getChannelModels(data.type));
        } else {
            showError(message);
        }
        setLoading(false);
    };

    const fetchModels = async () => {
        try {
            let res = await API.get(`/api/channel/models`);
            let localModelOptions = res.data.data.map((model) => ({
                label: model.id,
                value: model.id
            }));
            setOriginModelOptions(localModelOptions);
            setFullModels(res.data.data.map((model) => model.id));
        } catch (error) {
            showError(error.message);
        }
    };

    const fetchGroups = async () => {
        try {
            let res = await API.get(`/api/group/`);
            setGroupOptions(res.data.data.map((group) => ({
                label: group,
                value: group
            })));
        } catch (error) {
            showError(error.message);
        }
    };

    useEffect(() => {
        let localModelOptions = [...originModelOptions];
        inputs.models.forEach((model) => {
            if (!localModelOptions.find((option) => option.value === model)) {
                localModelOptions.push({
                    label: model,
                    value: model
                });
            }
        });
        setModelOptions(localModelOptions);
    }, [originModelOptions, inputs.models]);

    useEffect(() => {
        fetchModels().then();
        fetchGroups().then();
        if (isEdit) {
            loadChannel().then();
        } else {
            setInputs(originInputs);
            let localModels = getChannelModels(originInputs.type);
            setBasicModels(localModels);
        }
    }, [props.editingChannel.id]);

    const submit = async () => {
        if (inputs.key === '') {
            if (config.ak !== '' && config.sk !== '' && config.region !== '') {
                inputs.key = `${config.ak}|${config.sk}|${config.region}`;
            } else if (config.region !== '' && config.vertex_ai_project_id !== '' && config.vertex_ai_adc !== '') {
                inputs.key = `${config.region}|${config.vertex_ai_project_id}|${config.vertex_ai_adc}`;
            }
        }
        if (!isEdit && (inputs.name === '' || inputs.key === '')) {
            showInfo('请填写渠道名称和渠道密钥！');
            return;
        }
        if (inputs.type !== 43 && inputs.models.length === 0) {
            showInfo('请至少选择一个模型！');
            return;
        }
        if (inputs.model_mapping !== '' && !verifyJSON(inputs.model_mapping)) {
            showInfo('模型映射必须是合法的 JSON 格式！');
            return;
        }
        let localInputs = {...inputs};
        if (localInputs.key === 'undefined|undefined|undefined') {
            localInputs.key = '';
        }
        if (localInputs.base_url && localInputs.base_url.endsWith('/')) {
            localInputs.base_url = localInputs.base_url.slice(0, localInputs.base_url.length - 1);
        }
        if (localInputs.type === 3 && localInputs.other === '') {
            localInputs.other = '2024-03-01-preview';
        }
        if (localInputs.type === 18 && localInputs.other === '') {
            localInputs.other = 'v2.1';
        }
        if (!Array.isArray(localInputs.models)) {
            showError('提交失败，请勿重复提交！');
            handleCancel();
            return;
        }
        localInputs.auto_ban = autoBan ? 1 : 0;
        localInputs.models = localInputs.models.join(',');
        localInputs.group = localInputs.groups.join(',');
        localInputs.config = JSON.stringify(config);
        let res;
        if (isEdit) {
            res = await API.put(`/api/channel/`, {...localInputs, id: parseInt(channelId)});
        } else {
            res = await API.post(`/api/channel/`, localInputs);
        }
        const {success, message} = res.data;
        if (success) {
            if (isEdit) {
                showSuccess('渠道更新成功！');
            } else {
                showSuccess('渠道创建成功！');
                setInputs(originInputs);
            }
            props.refresh();
            props.handleClose();
        } else {
            showError(message);
        }
    };

    const addCustomModel = () => {
        if (customModel.trim() === '') return;
        if (inputs.models.includes(customModel)) return showError('该模型已存在！');
        let localModels = [...inputs.models];
        localModels.push(customModel);
        setModelOptions(modelOptions => {
            return [...modelOptions, {label: customModel, value: customModel}];
        });
        setCustomModel('');
        handleInputChange('models', localModels);
    };

    return (
        <>
            <SideSheet
                maskClosable={false}
                placement={isEdit ? 'right' : 'left'}
                title={<Title level={3}>{isEdit ? '更新渠道信息' : '创建新的渠道'}</Title>}
                headerStyle={{borderBottom: '1px solid var(--semi-color-border)'}}
                bodyStyle={{borderBottom: '1px solid var(--semi-color-border)'}}
                visible={props.visible}
                footer={
                    <div style={{display: 'flex', justifyContent: 'flex-end'}}>
                        <Space>
                            <Button theme='solid' size={'large'} onClick={submit}>提交</Button>
                            <Button theme='solid' size={'large'} type={'tertiary'} onClick={handleCancel}>取消</Button>
                        </Space>
                    </div>
                }
                closeIcon={null}
                onCancel={() => handleCancel()}
                width={isMobile() ? '100%' : 600}
            >
                <Spin spinning={loading}>
                    <div style={{marginTop: 10}}>
                        <Typography.Text strong>类型：</Typography.Text>
                    </div>
                    <Select
                        name='type'
                        required
                        optionList={CHANNEL_OPTIONS}
                        value={inputs.type}
                        onChange={value => handleInputChange('type', value)}
                        style={{width: '50%'}}
                    />

                    {/* Azure OpenAI */}
                    {inputs.type === 3 && (
                        <>
                            <div style={{marginTop: 10}}>
                                <Banner type='warning' description={
                                    <>
                                        注意，<strong>模型部署名称必须和模型名称保持一致</strong>，因为 Opcache 会把请求体中的
                                        model 参数替换为你的部署名称（模型名称中的点会被剔除），
                                        <a target='_blank'
                                           href='https://github.com/songquanpeng/one-api/issues/133?notification_referrer_id=NT_kwDOAmJSYrM2NjIwMzI3NDgyOjM5OTk4MDUw#issuecomment-1571602271'>图片演示</a>。
                                    </>
                                }/>
                            </div>
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>AZURE_OPENAI_ENDPOINT：</Typography.Text>
                            </div>
                            <Input
                                name='base_url'
                                placeholder='请输入 AZURE_OPENAI_ENDPOINT，例如：https://docs-test-001.openai.azure.com'
                                onChange={value => handleInputChange('base_url', value)}
                                value={inputs.base_url}
                                autoComplete='new-password'
                            />
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>默认 API 版本：</Typography.Text>
                            </div>
                            <Input
                                name='other'
                                placeholder='请输入默认 API 版本，例如：2024-03-01-preview，该配置可以被实际的请求查询参数所覆盖'
                                onChange={value => handleInputChange('other', value)}
                                value={inputs.other}
                                autoComplete='new-password'
                            />
                        </>
                    )}

                    {/* Custom channel base URL */}
                    {inputs.type === 8 && (
                        <>
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>Base URL：</Typography.Text>
                            </div>
                            <Input
                                name='base_url'
                                placeholder='请输入自定义渠道的 Base URL'
                                onChange={value => handleInputChange('base_url', value)}
                                value={inputs.base_url}
                                autoComplete='new-password'
                            />
                        </>
                    )}

                    {/* type 50 base URL */}
                    {inputs.type === 50 && (
                        <>
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>Base URL：</Typography.Text>
                            </div>
                            <Input
                                name='base_url'
                                placeholder='请输入 Base URL'
                                onChange={value => handleInputChange('base_url', value)}
                                value={inputs.base_url}
                                autoComplete='new-password'
                            />
                        </>
                    )}

                    <div style={{marginTop: 10}}>
                        <Typography.Text strong>名称：</Typography.Text>
                    </div>
                    <Input
                        required
                        name='name'
                        placeholder='请为渠道命名'
                        onChange={value => handleInputChange('name', value)}
                        value={inputs.name}
                        autoComplete='new-password'
                    />

                    <div style={{marginTop: 10}}>
                        <Typography.Text strong>分组：</Typography.Text>
                    </div>
                    <Select
                        placeholder='请选择可以使用该渠道的分组'
                        name='groups'
                        required
                        multiple
                        allowCreate
                        onChange={value => handleInputChange('groups', value)}
                        value={inputs.groups}
                        autoComplete='new-password'
                        optionList={groupOptions}
                        style={{width: '100%'}}
                    />

                    {/* Spark version */}
                    {inputs.type === 18 && (
                        <>
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>模型版本：</Typography.Text>
                            </div>
                            <Input
                                name='other'
                                placeholder='请输入星火大模型版本，注意是接口地址中的版本号，例如：v2.1'
                                onChange={value => handleInputChange('other', value)}
                                value={inputs.other}
                                autoComplete='new-password'
                            />
                        </>
                    )}

                    {/* Knowledge base ID */}
                    {inputs.type === 21 && (
                        <>
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>知识库 ID：</Typography.Text>
                            </div>
                            <Input
                                name='other'
                                placeholder='请输入知识库 ID，例如：123456'
                                onChange={value => handleInputChange('other', value)}
                                value={inputs.other}
                                autoComplete='new-password'
                            />
                        </>
                    )}

                    {/* Coze plugin param */}
                    {inputs.type === 17 && (
                        <>
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>插件参数：</Typography.Text>
                            </div>
                            <Input
                                name='other'
                                placeholder='请输入插件参数'
                                onChange={value => handleInputChange('other', value)}
                                value={inputs.other}
                                autoComplete='new-password'
                            />
                        </>
                    )}

                    {/* Coze notice */}
                    {inputs.type === 34 && (
                        <div style={{marginTop: 10}}>
                            <Banner type='info' description='Coze 渠道请填写 Bot ID 作为密钥'/>
                        </div>
                    )}

                    {/* Doubao notice */}
                    {inputs.type === 40 && (
                        <div style={{marginTop: 10}}>
                            <Banner type='info' description={
                                <>
                                    请先在
                                    <a target='_blank'
                                       href='https://console.volcengine.com/ark/region:ark+cn-beijing/endpoint'>
                                        火山方舟控制台
                                    </a>
                                    创建推理接入点，使用接入点 ID 作为模型名称。
                                </>
                            }/>
                        </div>
                    )}

                    {/* Model selection (not for type 43) */}
                    {inputs.type !== 43 && (
                        <>
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>模型：</Typography.Text>
                            </div>
                            <Select
                                placeholder='请选择该渠道所支持的模型'
                                name='models'
                                required
                                multiple
                                filter
                                onChange={value => handleInputChange('models', value)}
                                value={inputs.models}
                                autoComplete='new-password'
                                optionList={modelOptions}
                                style={{width: '100%'}}
                            />
                            <div style={{lineHeight: '40px', marginBottom: '12px', marginTop: '8px'}}>
                                <Space wrap>
                                    <Button type='primary' onClick={() => {
                                        handleInputChange('models', basicModels);
                                    }}>填入默认模型</Button>
                                    <Button type='secondary' onClick={() => {
                                        handleInputChange('models', fullModels);
                                    }}>填入所有模型</Button>
                                    <Button type='danger' onClick={() => {
                                        handleInputChange('models', []);
                                    }}>清除所有模型</Button>
                                </Space>
                                <div style={{marginTop: 8}}>
                                    <Input
                                        addonAfter={
                                            <Button type='primary' onClick={addCustomModel}>填入</Button>
                                        }
                                        placeholder='输入自定义模型名称'
                                        value={customModel}
                                        onChange={(value) => {
                                            setCustomModel(value.trim());
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                addCustomModel();
                                                e.preventDefault();
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>模型重定向：</Typography.Text>
                            </div>
                            <TextArea
                                placeholder={`此项可选，用于修改请求体中的模型名称，为一个 JSON 字符串，键为请求中模型名称，值为要替换的模型名称，例如：\n${JSON.stringify(MODEL_MAPPING_EXAMPLE, null, 2)}`}
                                name='model_mapping'
                                onChange={value => handleInputChange('model_mapping', value)}
                                autosize
                                value={inputs.model_mapping}
                                autoComplete='new-password'
                                style={{minHeight: 150, fontFamily: 'JetBrains Mono, Consolas'}}
                            />
                            <Typography.Text
                                style={{color: 'rgba(var(--semi-blue-5), 1)', userSelect: 'none', cursor: 'pointer'}}
                                onClick={() => {
                                    handleInputChange('model_mapping', JSON.stringify(MODEL_MAPPING_EXAMPLE, null, 2));
                                }}
                            >
                                填入模板
                            </Typography.Text>

                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>系统提示词：</Typography.Text>
                            </div>
                            <TextArea
                                placeholder='此项可选，用于强制设置给定的系统提示词，请配合自定义模型 & 模型重定向使用'
                                name='system_prompt'
                                onChange={value => handleInputChange('system_prompt', value)}
                                autosize
                                value={inputs.system_prompt}
                                autoComplete='new-password'
                                style={{minHeight: 150, fontFamily: 'JetBrains Mono, Consolas'}}
                            />
                        </>
                    )}

                    {/* AWS Bedrock config */}
                    {inputs.type === 33 && (
                        <>
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>Region：</Typography.Text>
                            </div>
                            <Input
                                name='region'
                                placeholder='请输入 AWS Region，例如：us-east-1'
                                onChange={value => handleConfigChange('region', value)}
                                value={config.region}
                                autoComplete=''
                            />
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>AK：</Typography.Text>
                            </div>
                            <Input
                                name='ak'
                                placeholder='请输入 AWS Access Key'
                                onChange={value => handleConfigChange('ak', value)}
                                value={config.ak}
                                autoComplete=''
                            />
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>SK：</Typography.Text>
                            </div>
                            <Input
                                name='sk'
                                placeholder='请输入 AWS Secret Key'
                                onChange={value => handleConfigChange('sk', value)}
                                value={config.sk}
                                autoComplete=''
                            />
                        </>
                    )}

                    {/* Vertex AI config */}
                    {inputs.type === 42 && (
                        <>
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>Region：</Typography.Text>
                            </div>
                            <Input
                                name='region'
                                placeholder='请输入 GCP Region，例如：us-central1'
                                onChange={value => handleConfigChange('region', value)}
                                value={config.region}
                                autoComplete=''
                            />
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>Vertex AI Project ID：</Typography.Text>
                            </div>
                            <Input
                                name='vertex_ai_project_id'
                                placeholder='请输入 GCP Project ID'
                                onChange={value => handleConfigChange('vertex_ai_project_id', value)}
                                value={config.vertex_ai_project_id}
                                autoComplete=''
                            />
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>Vertex AI 凭证（ADC JSON）：</Typography.Text>
                            </div>
                            <TextArea
                                name='vertex_ai_adc'
                                placeholder='请输入 GCP Application Default Credentials JSON'
                                onChange={value => handleConfigChange('vertex_ai_adc', value)}
                                value={config.vertex_ai_adc}
                                autosize
                                autoComplete=''
                            />
                        </>
                    )}

                    {/* Coze user_id */}
                    {inputs.type === 34 && (
                        <>
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>Bot ID：</Typography.Text>
                            </div>
                            <Input
                                name='user_id'
                                placeholder='请输入 Bot ID'
                                onChange={value => handleConfigChange('user_id', value)}
                                value={config.user_id}
                                autoComplete=''
                            />
                        </>
                    )}

                    {/* Cloudflare Account ID */}
                    {inputs.type === 37 && (
                        <>
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>Account ID：</Typography.Text>
                            </div>
                            <Input
                                name='user_id'
                                placeholder='请输入 Account ID，例如：d8d7c61dbc334c32d3ced580e4bf42b4'
                                onChange={value => handleConfigChange('user_id', value)}
                                value={config.user_id}
                                autoComplete=''
                            />
                        </>
                    )}

                    {/* API Key (not for AWS and Vertex) */}
                    {inputs.type !== 33 && inputs.type !== 42 && (
                        <>
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>密钥：</Typography.Text>
                            </div>
                            {batch ? (
                                <TextArea
                                    name='key'
                                    required
                                    placeholder='请输入密钥，一行一个'
                                    onChange={value => handleInputChange('key', value)}
                                    value={inputs.key}
                                    style={{minHeight: 150, fontFamily: 'JetBrains Mono, Consolas'}}
                                    autoComplete='new-password'
                                />
                            ) : (
                                <Input
                                    name='key'
                                    required
                                    placeholder={type2secretPrompt(inputs.type)}
                                    onChange={value => handleInputChange('key', value)}
                                    value={inputs.key}
                                    autoComplete='new-password'
                                />
                            )}
                        </>
                    )}

                    {/* Organization (OpenAI) */}
                    <div style={{marginTop: 10}}>
                        <Typography.Text strong>组织（可选）：</Typography.Text>
                    </div>
                    <Input
                        name='openai_organization'
                        placeholder='请输入组织 org-xxx（仅 OpenAI 渠道有效）'
                        onChange={value => handleInputChange('openai_organization', value)}
                        value={inputs.openai_organization}
                    />

                    {/* Auto ban toggle */}
                    <div style={{marginTop: 10, display: 'flex'}}>
                        <Space>
                            <Checkbox
                                name='auto_ban'
                                checked={autoBan}
                                onChange={() => setAutoBan(!autoBan)}
                            />
                            <Typography.Text strong>
                                是否自动禁用（仅当自动禁用开启时有效），关闭后不会自动禁用该渠道：
                            </Typography.Text>
                        </Space>
                    </div>

                    {/* Batch create toggle */}
                    {!isEdit && (
                        <div style={{marginTop: 10, display: 'flex'}}>
                            <Space>
                                <Checkbox
                                    checked={batch}
                                    name='batch'
                                    onChange={() => setBatch(!batch)}
                                />
                                <Typography.Text strong>批量创建</Typography.Text>
                            </Space>
                        </div>
                    )}

                    {/* Proxy URL (not for Azure/custom/FastGPT/type50/AWS/Vertex) */}
                    {inputs.type !== 3 && inputs.type !== 8 && inputs.type !== 22 && inputs.type !== 50 && inputs.type !== 33 && inputs.type !== 42 && (
                        <>
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>代理：</Typography.Text>
                            </div>
                            <Input
                                name='base_url'
                                placeholder='此项可选，用于通过代理站来进行 API 调用'
                                onChange={value => handleInputChange('base_url', value)}
                                value={inputs.base_url}
                                autoComplete='new-password'
                            />
                        </>
                    )}

                    {/* FastGPT private deploy URL */}
                    {inputs.type === 22 && (
                        <>
                            <div style={{marginTop: 10}}>
                                <Typography.Text strong>私有部署地址：</Typography.Text>
                            </div>
                            <Input
                                name='base_url'
                                placeholder='请输入私有部署地址，格式为：https://fastgpt.run/api/openapi'
                                onChange={value => handleInputChange('base_url', value)}
                                value={inputs.base_url}
                                autoComplete='new-password'
                            />
                        </>
                    )}
                </Spin>
            </SideSheet>
        </>
    );
};

export default EditChannel;
